import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminOrganizationService } from '../../../services/admin/admin-organization.service';
import { AdminSchoolService } from '../../../services/admin/admin-school.service';
import { OrganizationModel } from '../../../models/school/organization.model';
import { SchoolModel, SchoolFilter } from '../../../models/school/school.model';
import { CountryModel } from '../../../models/localization-and-pricing/country.model';
import { ModalService } from '../../../shared/modals/modal/modal.service';

@Component({
  selector: 'app-admin-organization-associate',
  templateUrl: './admin-organization-associate.component.html',
  styleUrls: ['./admin-organization-associate.component.css']
})
export class AdminOrganizationAssociateComponent implements OnInit {

  step = 1;
  organization: OrganizationModel = null;
  organizationId: string;

  allSchools: SchoolModel[] = [];
  selected = new Set<string>();
  private selectedSchoolsById = new Map<string, SchoolModel>();

  filterSearch = '';

  isLoadingOrganization = false;
  isLoadingSchools = false;
  isLoadingMore = false;
  isAssociating = false;
  hasMore = false;

  private readonly PAGE_SIZE = 50;
  private currentSkip = 0;
  private searchDebounceTimer: any;

  associateResult: { added: number, failed: number } = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private organizationSv: AdminOrganizationService,
    private schoolSv: AdminSchoolService,
    private modalSv: ModalService
  ) {}

  ngOnInit() {
    this.organizationId = this.route.snapshot.params['organizationId'];
    this.isLoadingOrganization = true;
    this.organizationSv.getOrganization(this.organizationId).subscribe(organization => {
      this.organization = organization;
      this.isLoadingOrganization = false;
      this.loadSchools();
    }, () => {
      this.isLoadingOrganization = false;
      this.modalSv.showAlertModal('Error', 'Could not load the organization. Please go back and try again.');
    });
  }

  loadSchools() {
    if (!this.organization) {
      return;
    }
    this.allSchools = [];
    this.currentSkip = 0;
    this.hasMore = false;
    this.isLoadingSchools = true;
    this.fetchPage(false);
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMore) {
      return;
    }
    this.isLoadingMore = true;
    this.fetchPage(true);
  }

  onSearchChange(value: string) {
    this.filterSearch = value;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadSchools(), 300);
  }

  private fetchPage(append: boolean) {
    const filter = new SchoolFilter();
    filter.country = this.organization.country;
    filter.search = this.filterSearch || undefined;
    filter.limit = this.PAGE_SIZE;
    filter.skip = this.currentSkip;
    if (this.organization.schoolIds && this.organization.schoolIds.length > 0) {
      filter.excludeSchoolIds = this.organization.schoolIds.join(',');
    }
    this.schoolSv.fetchSchools(filter).subscribe(schools => {
      if (append) {
        this.allSchools = this.allSchools.concat(schools);
        this.isLoadingMore = false;
      } else {
        this.allSchools = schools;
        this.isLoadingSchools = false;
      }
      this.hasMore = schools.length === this.PAGE_SIZE;
      this.currentSkip = this.currentSkip + schools.length;
    }, () => {
      this.isLoadingSchools = false;
      this.isLoadingMore = false;
      this.modalSv.showAlertModal('Error', 'Could not load schools. Please try again.');
    });
  }

  get filteredSchools(): SchoolModel[] {
    return this.allSchools;
  }

  get selectedSchools(): SchoolModel[] {
    return Array.from(this.selectedSchoolsById.values());
  }

  get schoolsAlreadyInThisOrg(): SchoolModel[] {
    return this.selectedSchools.filter(s => s.organizationId === this.organizationId);
  }

  get schoolsMovingFromAnotherOrg(): SchoolModel[] {
    return this.selectedSchools.filter(s => s.organizationId && s.organizationId !== this.organizationId);
  }

  isInAnotherOrg(school: SchoolModel): boolean {
    return !!school.organizationId && school.organizationId !== this.organizationId;
  }

  isInThisOrg(school: SchoolModel): boolean {
    return school.organizationId === this.organizationId;
  }

  toggleRow(id: string) {
    if (this.selected.has(id)) {
      this.selected.delete(id);
      this.selectedSchoolsById.delete(id);
    } else {
      this.selected.add(id);
      const school = this.allSchools.find(s => s._id === id);
      if (school) {
        this.selectedSchoolsById.set(id, school);
      }
    }
  }

  isSelected(id: string): boolean {
    return this.selected.has(id);
  }

  get allVisibleSelected(): boolean {
    const vis = this.filteredSchools;
    return vis.length > 0 && vis.every(s => this.selected.has(s._id));
  }

  get someVisibleSelected(): boolean {
    return this.filteredSchools.some(s => this.selected.has(s._id));
  }

  toggleSelectAllVisible(checked: boolean) {
    this.filteredSchools.forEach(s => {
      if (checked) {
        this.selected.add(s._id);
        this.selectedSchoolsById.set(s._id, s);
      } else {
        this.selected.delete(s._id);
        this.selectedSchoolsById.delete(s._id);
      }
    });
  }

  clearSelection() {
    this.selected.clear();
    this.selectedSchoolsById.clear();
  }

  goToConfirm() {
    if (!this.selected.size) {
      return;
    }
    this.step = 2;
  }

  backToSelect() {
    this.step = 1;
  }

  confirmAssociate() {
    this.isAssociating = true;

    // Schools already in this exact organization don't need a network call — the API 400s on them.
    const idsToAdd = this.selectedSchools
      .filter(s => s.organizationId !== this.organizationId)
      .map(s => s._id);

    this.organizationSv.bulkAddSchoolsToOrganization(idsToAdd, this.organizationId).subscribe(result => {
      this.isAssociating = false;
      if (result.added === 0 && idsToAdd.length > 0) {
        this.modalSv.showAlertModal(
          'Association failed',
          'None of the selected schools could be associated. Please try again.'
        );
        return;
      }
      this.associateResult = { added: result.added, failed: result.failed };
      this.step = 3;
    }, () => {
      this.isAssociating = false;
      this.modalSv.showAlertModal(
        'Association failed',
        'Something went wrong while associating schools. Please try again.'
      );
    });
  }

  backToDetail() {
    this.router.navigate(['admin', 'organizations', this.organizationId, 'detail']);
  }

  associateMore() {
    this.selected.clear();
    this.selectedSchoolsById.clear();
    this.associateResult = null;
    this.step = 1;
    this.loadSchools();
  }

  countryTitle(code: string): string {
    return CountryModel.title(code);
  }

  countryIconUrl(code: string): string {
    return CountryModel.iconURL(code);
  }

  schoolInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name.substring(0, 2).toUpperCase();
  }
}
